import { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import {
  TextField,
  Button,
  Box,
  Stack,
  Autocomplete,
  CircularProgress,
} from '@mui/material'
import { usersApi } from '@/api/users'
import type { CreateGroupData, UpdateGroupData, StudentGroup, User } from '@/types'

const createGroupSchema = (t: (key: string) => string) => z.object({
  name: z
    .string()
    .min(1, t('groups.validation.nameRequired'))
    .max(255, t('groups.validation.maxLength')),
  description: z
    .string()
    .max(1000, t('groups.validation.descriptionMaxLength'))
    .optional()
    .or(z.literal('')),
  member_ids: z.array(z.number()).optional(),
})

interface GroupFormProps {
  initialValues?: StudentGroup
  onSubmit: (data: CreateGroupData | UpdateGroupData) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
}

export function GroupForm({
  initialValues,
  onSubmit,
  onCancel,
  isLoading,
}: GroupFormProps) {
  const { t } = useTranslation()
  const isEdit = !!initialValues
  const [students, setStudents] = useState<User[]>([])
  const [studentsLoading, setStudentsLoading] = useState(false)

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateGroupData | UpdateGroupData>({
    resolver: zodResolver(createGroupSchema(t)),
    defaultValues: initialValues
      ? { name: initialValues.name, description: initialValues.description ?? '' }
      : { name: '', description: '', member_ids: [] },
  })

  const memberIds = watch('member_ids') ?? []

  useEffect(() => {
    if (!isEdit) {
      setStudentsLoading(true)
      usersApi
        .getAll()
        .then((res) => setStudents(res.data))
        .finally(() => setStudentsLoading(false))
    }
  }, [isEdit])

  const handleFormSubmit = async (data: CreateGroupData | UpdateGroupData) => {
    const payload = { ...data } as CreateGroupData & UpdateGroupData
    if (isEdit || !payload.member_ids?.length) {
      delete payload.member_ids
    }
    await onSubmit(payload as CreateGroupData | UpdateGroupData)
  }

  const getUserLabel = (user: User) => {
    if (user.first_name && user.surname) {
      return `${user.surname} ${user.first_name} (${user.email})`
    }
    return user.email
  }

  const selectedUsers = students.filter((s) => memberIds.includes(s.id))

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack spacing={3} sx={{ pt: 1 }}>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('groups.form.name')}
              placeholder={t('groups.form.namePlaceholder')}
              error={!!errors.name}
              helperText={errors.name?.message}
              fullWidth
              required
            />
          )}
        />

        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('groups.form.description')}
              placeholder={t('groups.form.descriptionPlaceholder')}
              error={!!errors.description}
              helperText={errors.description?.message}
              fullWidth
              multiline
              rows={4}
            />
          )}
        />

        {!isEdit && (
          <Controller
            name="member_ids"
            control={control}
            render={() => (
              <Autocomplete
                multiple
                options={students}
                getOptionLabel={getUserLabel}
                value={selectedUsers}
                onChange={(_, newValue) => setValue('member_ids', newValue.map((u) => u.id))}
                loading={studentsLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('groups.form.members')}
                    placeholder={t('groups.form.membersPlaceholder')}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {studentsLoading ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          />
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={onCancel} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
          >
            {isEdit ? t('common.save') : t('groups.create')}
          </Button>
        </Box>
      </Stack>
    </form>
  )
}
